import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

export const ImageGallery = ({ images, mainImage, title }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Combine main image with additional images
  const allImages = [mainImage, ...images.filter(img => img !== mainImage)];

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % allImages.length);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  const goToImage = (index) => {
    setCurrentIndex(index);
  };

  if (!allImages || allImages.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* Main Image Display */}
      <div className="relative group">
        <div 
          className="relative h-56 sm:h-80 md:h-96 bg-slate-900 rounded-xl overflow-hidden cursor-pointer"
          onClick={() => setIsFullscreen(true)}
        >
          <img
            src={allImages[currentIndex]}
            alt={`${title} - Image ${currentIndex + 1}`}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            onError={(e) => {
              // Graceful fallback so a broken URL doesn't leave a black box on mobile.
              if (e?.target) {
                e.target.src = 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1200&q=80';
              }
            }}
          />
          
          {/* Navigation Arrows — visible on touch devices, hover-fade on desktop */}
          {allImages.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goToPrevious();
                }}
                className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-slate-900 p-2 sm:p-3 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110 opacity-90 md:opacity-0 md:group-hover:opacity-100"
                aria-label="Previous image"
              >
                <ChevronLeft size={22} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goToNext();
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-slate-900 p-2 sm:p-3 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110 opacity-90 md:opacity-0 md:group-hover:opacity-100"
                aria-label="Next image"
              >
                <ChevronRight size={22} />
              </button>
            </>
          )}

          {/* Image Counter */}
          {allImages.length > 1 && (
            <div className="absolute bottom-4 right-4 bg-black/70 text-white px-4 py-2 rounded-full text-sm font-semibold backdrop-blur-sm">
              {currentIndex + 1} / {allImages.length}
            </div>
          )}
        </div>
      </div>

      {/* Thumbnail Navigation */}
      {allImages.length > 1 && (
        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
          {allImages.map((image, index) => (
            <button
              key={image || `thumb-${index}`}
              onClick={() => goToImage(index)}
              className={`relative h-14 sm:h-20 rounded-lg overflow-hidden border-2 transition-all duration-300 transform hover:scale-105 ${
                currentIndex === index
                  ? 'border-emerald-600 ring-2 ring-emerald-300'
                  : 'border-slate-300 hover:border-emerald-400'
              }`}
            >
              <img
                src={image}
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => {
                  if (e?.target) {
                    e.target.src = 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=200&q=60';
                  }
                }}
              />
              {currentIndex === index && (
                <div className="absolute inset-0 bg-emerald-600/20"></div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Fullscreen Modal */}
      {isFullscreen && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4">
          <button
            onClick={() => setIsFullscreen(false)}
            className="absolute top-4 right-4 bg-white text-slate-900 p-2 rounded-full hover:bg-slate-100 transition-colors z-10"
          >
            <X size={24} />
          </button>

          <div className="relative w-full h-full flex items-center justify-center">
            <img
              src={allImages[currentIndex]}
              alt={`${title} - Fullscreen`}
              className="max-w-full max-h-full object-contain"
            />

            {allImages.length > 1 && (
              <>
                <button
                  onClick={goToPrevious}
                  className="absolute left-4 bg-white/90 hover:bg-white text-slate-900 p-4 rounded-full shadow-lg transition-all duration-300"
                >
                  <ChevronLeft size={32} />
                </button>
                <button
                  onClick={goToNext}
                  className="absolute right-4 bg-white/90 hover:bg-white text-slate-900 p-4 rounded-full shadow-lg transition-all duration-300"
                >
                  <ChevronRight size={32} />
                </button>
              </>
            )}
          </div>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/90 text-slate-900 px-6 py-3 rounded-full text-lg font-semibold backdrop-blur-sm">
            {currentIndex + 1} / {allImages.length}
          </div>
        </div>
      )}
    </div>
  );
};
